import { Box, Button, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ImageIcon from "@mui/icons-material/Image";
import { useState } from "react";
import { getPreSignedUrl } from "../../api/docs";
import { v4 as uuidv4 } from "uuid";

interface Props {
    block: TestimonialBlock;
    onChange: (content: any) => void;
    onDelete: () => void;
}

const TestimonialBlockEditor = ({ block, onChange, onDelete }: Props) => {
    const [uploading, setUploading] = useState(false);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const fileName = `page-images/${uuidv4()}-${file.name}`;
            const preSignedUrl = await getPreSignedUrl(fileName, file.type);
            
            await fetch(preSignedUrl, {
                method: "PUT",
                body: file,
                headers: {
                    "Content-Type": file.type,
                },
            });

            const imageUrl = `https://riverhawkoa-docs.s3.amazonaws.com/${fileName}`;
            onChange({
                ...block.content,
                authorImage: imageUrl,
            });
        } catch (error) {
            console.error("Failed to upload image:", error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <IconButton size="small" sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                        Testimonial Block
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            label="Quote"
                            value={block.content.quote || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    quote: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                        />
                        <TextField
                            label="Author Name"
                            value={block.content.author || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    author: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Author Title (optional)"
                            value={block.content.authorTitle || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    authorTitle: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        {block.content.authorImage ? (
                            <Box>
                                <img
                                    src={block.content.authorImage}
                                    alt={block.content.author}
                                    style={{ width: 80, height: 80, borderRadius: "50%" }}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    border: "2px dashed #ccc",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <ImageIcon sx={{ fontSize: 32, color: "#ccc" }} />
                            </Box>
                        )}
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={uploading}
                            size="small"
                        >
                            {uploading ? "Uploading..." : "Upload Author Image"}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageUpload}
                            />
                        </Button>
                    </Stack>
                </Box>
                <IconButton size="small" onClick={onDelete} color="error">
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
};

export default TestimonialBlockEditor;
